import React, { useState, useEffect } from 'react';
import toNumber from 'lodash/toNumber';
import { AccountDetails } from '@desquared/wave-components-library';
import { useRouter } from 'next/router';
import { validateInput } from '@/utils/accountDetails';
import { completeProfile } from '@/api/register';
import { createTokenCookie } from '@/utils/cookies';
import CountryCodeAside from '@/components/Aside/CountryCodeAside';
import { CountryCodeSideSheetStore } from '../../store/sideSheet';
import User from '@/store/user';
import { CompleteProfileIDStore } from '@/store/completeProfileId';
import { CountryCodeStore } from '@/store/countryCode';
import { RegisterFlowStore } from '@/store/otpid';
import { useTranslations } from 'next-intl';
import { updateUser as updateUserInContactPigeon } from '@/utils/contactPigeon';
import { LoginProviderStore } from '@/store/loginProvider';
import { IsFromLoginStore } from '@/store/isFromLogin';
import { questionnaireRedirectionLink } from '@/store/questionnaire';

import styles from './AccountDetailsPage.module.css';
import { shutdown } from '@intercom/messenger-js-sdk';

export default function AccounteDetailsPage({ config, radioButtonList, setRadioButtonList, logo, type }) {
  const [cardError, setCardError] = useState(false);
  const [cardErrorMsg, setCardErrorMsg] = useState(' ');

  const [firstName, setFirstName] = useState('');
  const [firstNameErrorText, setFirstNameErrorText] = useState('');
  const [hasFirstNameError, setHasFirstNameError] = useState(false);

  const [surName, setSurName] = useState('');
  const [surNameErrorText, setSurNameErrorText] = useState('');
  const [hasSurNameError, setHasSurNameError] = useState(false);

  const [phone, setPhone] = useState('');
  const [hasBottomError, setHasBottomError] = useState(false);
  const [bottomErrorText, setBottomErrorText] = useState('');
  const [isRegisterFlow, setIsRegisterFlow] = useState(false);

  const [completeProfileRequestId, setCompleteProfileRequestId] = useState('');
  const [phoneErrorText, setPhoneErrorText] = useState('');

  const [email, setEmail] = useState('');
  const [emailErrorText, setEmailErrorText] = useState('');
  const [hasEmailError, setHasEmailError] = useState(false);

  const router = useRouter();
  const [user, setUser] = useState({});
  const [acceptedTerms, setHasAcceptedTerms] = useState(false);
  const [notificationsCheckboxChecked, setNotificationsCheckBoxChecked] = useState(false);
  const [cardNumberValue, setCardNumberValue] = useState('');
  const [countryCode, setCountryCode] = useState(['']);
  const t = useTranslations('account_details');
  const r = useTranslations('common');

  const hasEmail =
    !config.auth.methods.some(el => el.method === 'MOBILE_PHONE' && el.active) ||
    LoginProviderStore.getValue() === 'GOOGLE' ||
    LoginProviderStore.getValue() === 'FACEBOOK' ||
    LoginProviderStore.getValue() === 'APPLE';
  const userConfig = config?.fields?.user;

  function propertyExists(arr, property) {
    return arr.some(item => item.property === property);
  }
  const hasLoyal = propertyExists(config.auth.profile.registration.userProperties, 'LOYALTY_CARD');

  useEffect(() => {
    const CountryCodeStore$ = CountryCodeStore.subscribe(code => setCountryCode(code));
    const RegisterFlowStore$ = RegisterFlowStore.subscribe(register => setIsRegisterFlow(register));

    const completeProfileRequestId$ = CompleteProfileIDStore.subscribe(_completeProfileRequestId => {
      if (!_completeProfileRequestId) return;
      setCompleteProfileRequestId(_completeProfileRequestId);
    });
    const user$ = User.subscribe(_user => {
      if (!_user) return;
      setUser(_user);
    });
    return () => {
      completeProfileRequestId$.unsubscribe();
      CountryCodeStore$.unsubscribe();
      user$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setFirstName(user.firstName ?? '');
    setSurName(user.lastName ?? '');
    setPhone(user.mobilePhone ?? '');
    setEmail(user.email ?? '');
    splitPhoneNumber(user.mobilePhone);
    if (user?.loyaltyCards?.length > 0) {
      setCardNumberValue(user.loyaltyCards[0].cardId);
      let newList = [...radioButtonList];
      newList[1].selected = true;
      newList[0].selected = false;
      setRadioButtonList(newList);
    }
  }, [user]);

  const clearAllErrors = () => {
    setHasBottomError(false);
    setBottomErrorText('');
    setPhoneErrorText('');
    setCardError(false);
    setCardErrorMsg(' ');
    setSurNameErrorText('');
    setHasSurNameError(false);
    setFirstNameErrorText('');
    setHasFirstNameError(false);
    setEmailErrorText('');
    setHasEmailError(false);
  };

  const onFirstNameChange = e => {
    setFirstName(e.currentTarget.value);
    clearAllErrors();
  };

  const onSurNameChange = e => {
    setSurName(e.currentTarget.value);
    clearAllErrors();
  };

  const onPhoneChange = e => {
    setPhone(e.currentTarget.value);
    clearAllErrors();
  };

  const onEmailChange = e => {
    setEmail(e.currentTarget.value);
    clearAllErrors();
  };

  const onCardNumberValueChange = e => {
    setCardNumberValue(e.currentTarget.value);
    clearAllErrors();
  };

  const submit = async () => {
    setHasBottomError(false);
    setBottomErrorText('');

    if (config?.services?.chatbot?.enabled && config?.services?.chatbot?.type === 'INTERCOM') {
      shutdown();
    }

    if (!firstName || firstName.trim() === '') {
      setFirstNameErrorText(r('checkout.required_field'));
      setHasFirstNameError(true);
      return;
    }

    if (!surName || surName.trim() === '') {
      setSurNameErrorText(r('checkout.required_field'));
      setHasSurNameError(true);
      return;
    }

    if (!email || email.trim() === '') {
      setEmailErrorText(r('checkout.required_field'));
      setHasEmailError(true);
      return;
    }

    const { data, error } = await completeProfile({
      completeProfileRequestId: completeProfileRequestId,
      user: {
        _id: user._id,
        role: user.role,
        email: user.email ? user.email : email,
        mobilePhone: user.mobilePhone ? user.mobilePhone : phone && phone.length > 0 ? countryCode[0] + phone : '',
        firstName: firstName,
        lastName: surName,
        loyaltyCards: hasLoyal
          ? cardNumberValue
            ? [{ cardId: cardNumberValue, points: [{ value: user?.totalLoyaltyPoints ?? 0 }], enabled: true }]
            : user.loyaltyCards
          : [],
        receiveNewsletters: notificationsCheckboxChecked,
        receiveChatbotMessages: notificationsCheckboxChecked,
      },
    });
    if (error) {
      if (error.code === 'USER0004') {
        setPhoneErrorText(t('errors.phone_format'));
        return;
      } else if (error.code === 'USER0003') {
        setPhoneErrorText(t('errors.phone_exists'));
        return;
      } else if (error.code === 'LOY0001') {
        setCardError(true);
        setCardErrorMsg(t('errors.loyalty_format'));
      } else if (error.code === 'LOY0002') {
        setHasBottomError(true);
        setBottomErrorText(t('errors.loyalty_exists'));
      } else {
        setHasBottomError(true);
        setBottomErrorText(t('generic_error'));
      }
    } else {
      if (!data) {
        setHasBottomError(true);
        setBottomErrorText(t('generic_error'));
        return;
      }
      if (!data?.authToken?.token) {
        setPhoneErrorText(t('errors.phone_format'));
        return;
      }
      createTokenCookie(data.authToken.token);
      User.update({
        ...data.user,
        authToken: data.authToken.token,
      });
      if (process.env.hasContactPigeon) {
        updateUserInContactPigeon(data.user.email);
      }
      if (process.env.hasStoreNecessary && (!data?.user?.addresses || data?.user?.addresses?.length === 0)) {
        router.push('/select-service');
        return;
      }
      IsFromLoginStore.update(true);

      if (questionnaireRedirectionLink.getValue() && questionnaireRedirectionLink.getValue().length > 0) {
        const questionnaireLink = questionnaireRedirectionLink.getValue();
        const [pathname, queryString] = questionnaireLink.split('?');

        // If there's a query string, parse it
        const params = new URLSearchParams(queryString);
        const customerId = params.get('customerId');
        questionnaireRedirectionLink.update('');
        router.push({
          pathname: pathname,
          query: { customerId: customerId },
        });
      } else {
        router.push({
          pathname: '/',
        });
      }
    }
  };

  const onRadioOptionClick = id => {
    let newList = [...radioButtonList];
    if (radioButtonList[toNumber(id)].selected) return;
    else {
      newList.map(radio => {
        radio.selected = false;
        if (radio.id === id) {
          radio.selected = true;
        }
      });
    }

    setRadioButtonList(newList);
  };

  const onCountryCodeClick = () => {
    if (user.mobilePhone) return;
    CountryCodeSideSheetStore.open();
  };

  function splitPhoneNumber(phoneNumber) {
    if (!phoneNumber) return;
    // Regular expression to capture the country code and the rest of the phone number
    const regex = /^(\+\d+)(\d{10})$/;
    const match = phoneNumber.match(regex);

    if (match) {
      setPhone(match[2]);
      setCountryCode([match[1]]);
    }
  }

  return (
    <>
      <div className={styles.container}>
        <AccountDetails
          logo={logo}
          logoRedirect={router.locale === router.defaultLocale ? '/' : `/${router.locale}/`}
          setFirstName={onFirstNameChange}
          setSurName={onSurNameChange}
          setPhone={onPhoneChange}
          setEmail={onEmailChange}
          onContinueClick={submit}
          phoneErrorText={phoneErrorText}
          phoneHelperTextType={phoneErrorText ? 'error' : 'success'}
          gdprLink={process.env.gdpr}
          termsLink={process.env.terms}
          continueText={t('continue')}
          personalDetailsTitleText={t('personal_details')}
          titleText={isRegisterFlow ? t('title_register') : t('title_update')}
          email={user.email}
          firstName={firstName}
          surName={surName}
          phone={phone}
          namePlaceHolder={t('name')}
          surnamePlaceHolder={t('last_name')}
          emailPlaceHolder={t('email')}
          phonePlaceHolder={t('phone')}
          description={isRegisterFlow ? t('info_register') : t('info_update')}
          acceptText={t('accept')}
          termsText={` ${t('terms')} `}
          extraText={t('extra')}
          gdprText={` ${t('gdpr')}`}
          userProperties={config.auth.profile.registration.userProperties}
          checkBoxChecked={acceptedTerms}
          checkCheckBox={() => setHasAcceptedTerms(!acceptedTerms)}
          isButtonDisabled={!acceptedTerms}
          hasLoyalty={hasLoyal}
          loyaltyTitleText="Loyalty Card"
          radioButtonList={radioButtonList}
          loyaltyDescription={t('loyalty_description')}
          loyaltySupportiveText={t('loyalty_supportive')}
          loyaltySupportMail={process.env.supportEmail}
          cardPattern="12      ΧΧΧΧΧ     ΧΧΧΧΧ"
          cardNumberValue={cardNumberValue}
          onCardNumberValueChange={onCardNumberValueChange}
          cardNumberInputLabel={t('card_label')}
          cardNumberInputPlaceHolder={t('card_placeholder')}
          cardNumberInputErrorText={cardErrorMsg}
          cardNumberInputErrorTextType={cardError ? 'error' : 'success'}
          onRadioOptionClick={onRadioOptionClick}
          onCardBlur={() => validateInput(cardNumberValue, config?.loyalty?.pattern, setCardError, setCardErrorMsg, t)}
          firstNameErrorText={firstNameErrorText}
          firstNameErrorType={hasFirstNameError ? 'error' : 'success'}
          surNameErrorText={surNameErrorText}
          surNameErrorType={hasSurNameError ? 'error' : 'success'}
          emailErrorText={emailErrorText}
          emailErrorType={hasEmailError ? 'error' : 'success'}
          hasBottomError={hasBottomError}
          bottomErrorText={bottomErrorText}
          onCodeClick={onCountryCodeClick}
          onOpenSideClick={onCountryCodeClick}
          selectedCodeIndex={0}
          countryCodeLabel={t('country_code')}
          countries={countryCode}
          classOption={type === 1 || type === 2 ? styles.paddingContainer : styles.noPaddingContainer}
          hasEmail={hasEmail}
          userConfig={userConfig}
          hasNotifications={config?.services?.newsletter?.enabled}
          checkNotificationsCheckbox={() => setNotificationsCheckBoxChecked(!notificationsCheckboxChecked)}
          notificationsCheckboxChecked={notificationsCheckboxChecked}
          notificationsText={t('accept_notifications')}
        />
      </div>
      <CountryCodeAside />
    </>
  );
}
